import {
  addToaster,
  getGenericEntityListingAPI,
  hideLoader,
  showLoader,
  WORKFLOW_UI_IDENTIFIER,
  WorkflowInstanceTaskType,
} from '@app-store'
import { CustomButton, FormInput, MultiLineInput } from '@ui-kit'
import { FC, useEffect, useState } from 'react'
import { FieldValues, useForm } from 'react-hook-form'
import { v4 as uniqueId } from 'uuid'
import { editEntityDataEmbeddedAPI } from '../../apis/entityData.api'
import {
  INTAKE_DOCUMENT,
  NAMESPACES,
  TASK_STATUS,
  WORKFLOW_STATUS,
} from '../../utils/constants'
import {
  formatNumberTwoDigits,
  logErrorMessage,
  permissionObject,
} from '../../utils/helperFunctions'
import { currentUserId, isReadOnlyUser } from '../../utils/permission'
import {
  RootState,
  useAppDispatch,
  useAppSelector,
} from '../../utils/agent.store'
import { SystemAutoTaskPropsType } from '../../types/workflow.types'
import { STRINGS } from '../../utils/strings'

const ReceiveInboundTask: FC<SystemAutoTaskPropsType> = ({
  task,
  reFetch,
  tabIndexOffset,
  isDisabled = false,
}) => {
  const dispatch = useAppDispatch()
  const [notesState, setNotesState] = useState<boolean>(false)
  const [taskNoteBoxId, setTaskNoteBoxId] = useState<string | undefined>()
  const [taskNotesData, setTaskNotesData] = useState<FieldValues>()
  const { setValue, register, getValues, watch } = useForm()
  const mode = notesState || !taskNotesData ? 'edit' : 'view'
  const workflowDefinition = useAppSelector(
    (state) => state?.workflowData?.workflowDefinition
  )
  const programPermissions = useAppSelector(
    (state: RootState) => state.clientPermissions?.programPermissions
  )
  const caseData = useAppSelector(
    (state: RootState) => state.workflowData?.caseData
  )
  const userId = useAppSelector((state: RootState) => state?.authUser?.user?.id)

  const fetchTaskNotesData = async () => {
    try {
      dispatch(showLoader())
      const payload = {
        entity_name:
          workflowDefinition?.global_variables?.entities?.task?.entity_name ??
          NAMESPACES.TASK,
        select: ['c_case_notes_task', NAMESPACES.NAME_KEY],
        filters: [{ criteria: [] }],
        related_entities: {
          c_case_notes_task: {
            select: [NAMESPACES.NAME_KEY],
            criteria: [{ criteria: [] }],
          },
        },
      }
      const responseData = await getGenericEntityListingAPI(payload, task?.id)
      const noteText =
        responseData?.c_case_notes_task?.[0]?.name ?? task?.notes ?? ''
      setTaskNotesData(noteText)
      if (responseData?.c_case_notes_task?.[0]?.id) {
        setTaskNoteBoxId(
          responseData?.c_case_notes_task?.[0]?.id ?? task?.notes
        )
      }
      setValue(`taskNotes${task?.task_id}`, noteText)
    } catch (error) {
      dispatch(
        addToaster({
          message: NAMESPACES.SOMETHING_WENT_WRONG,
          type: 'error',
          id: uniqueId(),
        })
      )
      logErrorMessage('taskNotes', error)
    } finally {
      dispatch(hideLoader())
    }
  }

  useEffect(() => {
    if (workflowDefinition && task) {
      void fetchTaskNotesData()
    }
  }, [workflowDefinition, task])

  useEffect(() => {
    if (task) {
      setValue(
        `documentId${task?.task_id}`,
        task.identifier?.ui_identifier ===
          WORKFLOW_UI_IDENTIFIER?.INTERACTION_INBOUND_TASK
          ? task?.identifier?.interaction_id
          : task?.identifier?.document_id ?? ''
      )
      setValue(
        `sourceIntakeId${task?.task_id}`,
        task.identifier?.ui_identifier ===
          WORKFLOW_UI_IDENTIFIER?.INTERACTION_INBOUND_TASK
          ? task?.identifier?.aws_contact_id
          : task?.identifier?.source_intake_id ?? ''
      )
    }
  }, [task, setValue])

  const updateTask = async () => {
    try {
      dispatch(showLoader())
      await editEntityDataEmbeddedAPI(
        workflowDefinition?.global_variables?.entities?.task?.entity_name ??
          NAMESPACES.TASK,
        task?.id ?? '',
        {
          c_case_notes_task: [
            {
              name: getValues(`taskNotes${task?.task_id}`),
              source: NAMESPACES.TASK,
              headers: `T${formatNumberTwoDigits(
                task?.task_id ? +task?.task_id : 0
              )}- ${task?.name}`,
              ...(taskNoteBoxId ? { id: taskNoteBoxId } : {}),
              _state: NAMESPACES.CHANGED,
              case: {
                id: task?.case_id,
                _state: NAMESPACES.CHANGED,
              },
              ...(!taskNoteBoxId ? { users_id: currentUserId() ?? '' } : {}),
            },
          ],
          record_status: TASK_STATUS.ACTIVE,
        },
        ['c_case_notes_task']
      )
      dispatch(
        addToaster({
          message: NAMESPACES.TASK_NOTES_SAVED,
          type: 'success',
          id: uniqueId(),
        })
      )
      reFetch?.()
    } catch (error) {
      dispatch(
        addToaster({
          message: NAMESPACES.SOMETHING_WENT_WRONG,
          type: 'error',
          id: uniqueId(),
        })
      )
      logErrorMessage('taskNotes', error)
    } finally {
      setNotesState(false)
      dispatch(hideLoader())
    }
  }

  const canEditNotes =
    permissionObject.Manage_Workflow(
      programPermissions,
      caseData,
      task as WorkflowInstanceTaskType,
      userId ?? ''
    ) &&
    !isReadOnlyUser() &&
    task?.state === WORKFLOW_STATUS.OPEN &&
    !isDisabled

  if (!task?.identifier?.taskNoteValues?.isTaskNoteSuggestionBox) {
    return null
  }

  return (
    <div className="p-2 bg-accent-0">
      <div className="flex gap-4 pb-4">
        <div className="flex-1">
          <FormInput
            register={register}
            name={`documentId${task?.task_id}`}
            label={
              task.identifier.ui_identifier ===
              WORKFLOW_UI_IDENTIFIER?.INTERACTION_INBOUND_TASK
                ? INTAKE_DOCUMENT.INTERACTION_ID
                : INTAKE_DOCUMENT.DOCUMENT_ID
            }
            disabled={true}
            validation={{ required: true }}
          />
        </div>
        <div className="flex-1">
          <FormInput
            register={register}
            name={`sourceIntakeId${task?.task_id}`}
            label={
              task.identifier.ui_identifier ===
              WORKFLOW_UI_IDENTIFIER?.INTERACTION_INBOUND_TASK
                ? INTAKE_DOCUMENT.AWS_CONTACT_ID
                : task.identifier.ui_identifier ===
                  WORKFLOW_UI_IDENTIFIER?.WEB_INBOUND_TASK
                ? INTAKE_DOCUMENT.ENROLLMENT_ID
                : INTAKE_DOCUMENT.SOURCE_INTAKE_ID
            }
            disabled={true}
            validation={{ required: true }}
          />
        </div>
      </div>
      <div className="flex flex-row-reverse pb-2 gap-2">
        {canEditNotes ? (
          !notesState && taskNotesData ? (
            <CustomButton
              isFilled
              color="primary"
              onClick={() => setNotesState(true)}
              tabIndex={tabIndexOffset}
            >
              {STRINGS.EDIT}
            </CustomButton>
          ) : (
            <CustomButton
              isFilled
              color="primary"
              onClick={updateTask}
              tabIndex={tabIndexOffset}
              disabled={!watch(`taskNotes${task?.task_id}`)?.trim()}
            >
              {STRINGS.SAVE}
            </CustomButton>
          )
        ) : null}
        {canEditNotes && mode !== 'view' ? (
          <CustomButton
            disabled={!watch(`taskNotes${task?.task_id}`)?.trim()}
            color="primary"
            onClick={() => setValue(`taskNotes${task?.task_id}`, '')}
            tabIndex={tabIndexOffset}
          >
            {STRINGS.CLEAR}
          </CustomButton>
        ) : null}
      </div>
      <MultiLineInput
        className="w-full border rounded p-4 min-h-20 overflow-y-auto"
        name={`taskNotes${task?.task_id}`}
        widthClass="w-full"
        disabled={mode === 'view' || !canEditNotes}
        register={register}
        id="editing-note"
        label={STRINGS.NOTES}
        validation={{ required: true }}
      />
    </div>
  )
}

export default ReceiveInboundTask
